const ORIENTATIONS = ["portrait", "landscape"];
const PAGE_DIMENSIONS = { width: "auto", height: "auto", "min-width": "0", "min-height": "0", "max-width": "none", "max-height": "none" };
const listeners = new Set();
let active = null;

const snapshot = () => ({ printing: !!active, orientation: active ? active.orientation : null });
const notify = () => listeners.forEach(listener => {
    // A view observer must not interrupt restoration of the native print lifecycle.
    try { listener(snapshot()); } catch (_) { /* The observer does not own printing. */ }
});

export function subscribePrintOrientation(listener) {
    if (typeof listener !== "function") throw new TypeError("Pengamat cetak tidak sah.");
    listeners.add(listener);
    listener(snapshot());
    return () => listeners.delete(listener);
}

function pageDeclarations(document) {
    const declarations = [];
    const seen = new Set();
    const visit = rules => Array.from(rules || []).forEach(rule => {
        if (seen.has(rule)) return;
        seen.add(rule);
        if (rule.style && /^@page\b/i.test(rule.cssText || "")) declarations.push(rule.style);
        try { if (rule.cssRules) visit(rule.cssRules); } catch (_) { /* Cross-origin rules cannot be changed. */ }
        try { if (rule.styleSheet) visit(rule.styleSheet.cssRules); } catch (_) { /* Keep external sheets intact. */ }
    });
    [...Array.from(document.styleSheets || []), ...Array.from(document.adoptedStyleSheets || [])].forEach(sheet => {
        try { visit(sheet.cssRules); } catch (_) { /* Font/CDN sheets are outside this document's CSS access. */ }
    });
    return declarations;
}

export function printWithOrientation(orientation) {
    if (!ORIENTATIONS.includes(orientation)) throw new TypeError("Pilih portrait atau landscape.");
    if (active) throw new Error("Pencetakan masih berjalan.");
    if (typeof window === "undefined" || !window.document || typeof window.print !== "function") {
        throw new Error("Pencetakan tidak tersedia pada perangkat ini.");
    }
    const document = window.document;
    const previous = pageDeclarations(document).map(style => ({ style,
        properties: ["size", ...Object.keys(PAGE_DIMENSIONS)].map(property => ({ property,
            value: style.getPropertyValue(property), priority: style.getPropertyPriority(property) }))
    }));
    const fallback = document.createElement("style");
    fallback.setAttribute("data-eduprime-print-orientation", orientation);
    fallback.textContent = `@page { size: A4 ${orientation} !important; }`;
    const marker = "data-eduprime-print-orientation";
    const hadMarker = document.documentElement.hasAttribute(marker);
    const oldMarker = document.documentElement.getAttribute(marker);
    let disposed = false;
    let media;
    let entered = false;
    const beforePrint = () => { entered = true; };
    const mediaChange = event => { if (event.matches) entered = true; else if (entered) cleanup(); };
    const request = { orientation, cleanup: () => cleanup() };
    function cleanup() {
        if (disposed) return;
        disposed = true;
        window.removeEventListener("afterprint", cleanup);
        window.removeEventListener("beforeprint", beforePrint);
        window.removeEventListener("pagehide", cleanup);
        if (media) {
            if (media.removeEventListener) media.removeEventListener("change", mediaChange);
            else if (media.removeListener) media.removeListener(mediaChange);
        }
        try {
            previous.forEach(({ style, properties }) => {
                properties.forEach(({ property, value, priority }) => {
                    try {
                        if (value) style.setProperty(property, value, priority);
                        else style.removeProperty(property);
                    } catch (_) { /* A detached/read-only rule must not block the other restorations. */ }
                });
            });
        } finally {
            fallback.remove();
            if (hadMarker) document.documentElement.setAttribute(marker, oldMarker);
            else document.documentElement.removeAttribute(marker);
            if (active === request) active = null;
            notify();
        }
    }
    try {
        previous.forEach(({ style }) => {
            style.setProperty("size", `A4 ${orientation}`, "important");
            Object.keys(PAGE_DIMENSIONS).forEach(property => {
                if (style.getPropertyValue(property)) style.setProperty(property, PAGE_DIMENSIONS[property], "important");
            });
        });
        document.head.insertBefore(fallback, document.head.firstChild);
        document.documentElement.setAttribute(marker, orientation);
        active = request;
        window.addEventListener("afterprint", cleanup);
        window.addEventListener("beforeprint", beforePrint);
        window.addEventListener("pagehide", cleanup);
        if (typeof window.matchMedia === "function") {
            media = window.matchMedia("print");
            if (media.addEventListener) media.addEventListener("change", mediaChange);
            else if (media.addListener) media.addListener(mediaChange);
        }
        notify();
        window.print();
        return cleanup;
    } catch (error) {
        cleanup();
        throw error;
    }
}
